import { useMemo } from "react";
import { Box, Checkbox, FormControlLabel, Typography } from "@mui/material";
import styled from "@emotion/styled";
import { useTranslation } from "react-i18next";
import { useChartColors } from "@hooks";
import { useBottomControlsSectionCollapse } from "@rentablo/components";
import { SecuritiesChartSelectedType } from "../../SecuritiesChart/types";
import { canShowAssetTypes, useChartControls } from "../ChartControlsContext";
import { CONSTRAINED_MAX_WIDTH } from "./constrainedWidth";
import type { CSSObject } from "@emotion/styled";
import type { Theme } from "@mui/material/styles";

const getScrollableStyles = (theme: Theme): CSSObject => ({
  overflowX: "auto",
  paddingBottom: theme.spacing(1),
  "&::-webkit-scrollbar": {
    height: "4px",
  },
  "&::-webkit-scrollbar-track": {
    background: "transparent",
  },
  "&::-webkit-scrollbar-thumb": {
    background: theme.palette.divider,
    borderRadius: "2px",
  },
});

const Container = styled(Box, {
  shouldForwardProp: (prop) => prop !== "scrollable" && prop !== "constrained",
})<{ scrollable?: boolean; constrained?: boolean }>(
  ({ theme, scrollable, constrained }) => ({
    display: "flex",
    flexWrap: scrollable ? "nowrap" : "wrap",
    justifyContent: "flex-start",
    gap: theme.spacing(1),
    marginLeft: theme.spacing(2),
    maxWidth: constrained ? CONSTRAINED_MAX_WIDTH : undefined,
    [theme.breakpoints.down("sm")]: {
      marginBottom: theme.spacing(1),
    },
    ...(scrollable && getScrollableStyles(theme)),
  })
);

const CheckboxLabelContent = styled("span")(({ theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: theme.spacing(2),
}));

const ColorDot = styled("span")<{ color?: string }>(({ theme, color }) => ({
  width: "8px",
  height: "8px",
  borderRadius: "50%",
  backgroundColor: color || theme.palette.divider,
}));

interface ChartControlItem {
  text: string;
  value: SecuritiesChartSelectedType;
  clickable?: boolean;
}

interface Props {
  scrollable?: boolean;
  constrainWidth?: boolean;
  allTypeColor?: string;
}

export const AssetTypeControl = ({
  scrollable = false,
  constrainWidth = false,
  allTypeColor,
}: Props) => {
  const { t } = useTranslation();
  const ctx = useChartControls();
  const { selectedAssetTypes, setSelectedAssetTypes, availableAssetTypes } =
    ctx;
  const colors = useChartColors("securitiesChart");
  const collapseSection = useBottomControlsSectionCollapse();

  const chartColors = useMemo(() => {
    const stringColors = Object.fromEntries(
      Object.entries(colors).filter(([, value]) => typeof value === "string")
    ) as Record<string, string>;
    return allTypeColor ? { ...stringColors, all: allTypeColor } : stringColors;
  }, [colors, allTypeColor]);

  const chartControlItems = useMemo<ChartControlItem[]>(
    () =>
      (["all"] as SecuritiesChartSelectedType[])
        .concat(availableAssetTypes)
        .map((i) => ({
          text: t(`general.chart.securitiesChart.assetTypes.${i}`),
          value: i,
          clickable: selectedAssetTypes.includes(i)
            ? selectedAssetTypes.length >= 2
            : true,
        })),
    [availableAssetTypes, selectedAssetTypes, t]
  );

  if (!canShowAssetTypes(ctx)) {
    return null;
  }

  const handleToggleType = (value: SecuritiesChartSelectedType) => {
    if (selectedAssetTypes.includes(value)) {
      setSelectedAssetTypes(selectedAssetTypes.filter((i) => i !== value));
    } else {
      setSelectedAssetTypes([...selectedAssetTypes, value]);
    }
    collapseSection();
  };

  return (
    <Container scrollable={scrollable} constrained={constrainWidth}>
      {chartControlItems.map((type) => {
        const checked = selectedAssetTypes.includes(type.value);
        const color = chartColors[type.value];

        return (
          <FormControlLabel
            key={`filter-${type.value}`}
            control={
              <Checkbox
                size="small"
                checked={checked}
                onChange={() => type.clickable && handleToggleType(type.value)}
              />
            }
            label={
              <CheckboxLabelContent>
                <ColorDot color={color} />
                <Typography variant="body3">{type.text}</Typography>
              </CheckboxLabelContent>
            }
          />
        );
      })}
    </Container>
  );
};
